/**
 * Hotel Bull API module
 */
import { coreClient, apiRequest } from '../client.js';
import type {
  JsonValue,
  RoastProfile,
  HotelBullStats,
  HotelBullActivityHistory,
  HotelBullLlmStats,
  HotelBullBotHealth,
  HealthStatus,
} from '../types.js';

export const hotelBullApi = {
  health: () =>
    apiRequest<{ status: HealthStatus; timestamp: string }>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/health',
    }),

  getProfile: (hashedUserId: string, guildId: string) =>
    apiRequest<RoastProfile>(coreClient, {
      method: 'GET',
      url: `/hotel-bull/profile/${hashedUserId}/${guildId}`,
    }),

  getSettings: (guildId: string) =>
    apiRequest<Record<string, JsonValue>>(coreClient, {
      method: 'GET',
      url: `/hotel-bull/settings/${guildId}`,
    }),

  getTargetSettings: (hashedUserId: string, guildId: string) =>
    apiRequest<Record<string, JsonValue>>(coreClient, {
      method: 'GET',
      url: `/hotel-bull/target/${hashedUserId}/${guildId}`,
    }),

  updateProfile: (profile: Partial<RoastProfile>) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/profile',
      data: profile,
    }),

  deleteProfile: (hashedUserId: string, guildId: string) =>
    apiRequest(coreClient, {
      method: 'DELETE',
      url: `/hotel-bull/profile/${hashedUserId}/${guildId}`,
    }),

  toggleProfile: (hashedUserId: string, guildId: string, enabled: boolean) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/profile/toggle',
      data: { hashedUserId, guildId, enabled },
    }),

  updateChannelExclusion: (hashedUserId: string, guildId: string, channelId: string, exclude: boolean) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/profile/channel-exclusion',
      data: { hashedUserId, guildId, channelId, exclude },
    }),

  setTarget: (hashedUserId: string, guildId: string, enabled: boolean) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/target/set',
      data: { hashedUserId, guildId, enabled },
    }),

  updateTargetStats: (hashedUserId: string, guildId: string) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/target/stats',
      data: { hashedUserId, guildId },
    }),

  setReplyChannel: (guildId: string, channelId: string) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/settings/reply-channel',
      data: { guildId, channelId },
    }),

  setTargetOnlyMode: (guildId: string, enabled: boolean) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/settings/target-only',
      data: { guildId, enabled },
    }),

  updateRoastSessionComeback: (sessionId: string, userReply: string, botComeback: string) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/hotel-bull/session/comeback',
      data: { sessionId, userReply, botComeback },
    }),

  getStats: (guildId?: string) =>
    apiRequest<HotelBullStats>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/stats',
      params: { guildId },
    }),

  getActivityHistory: (guildId?: string, days: number = 7) =>
    apiRequest<HotelBullActivityHistory>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/activity-history',
      params: { guildId, days },
    }),

  getRecentProfiles: (limit: number = 10) =>
    apiRequest<{
      profiles: RoastProfile[];
      total: number;
      limit: number;
      timestamp: string;
    }>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/profiles/recent',
      params: { limit },
    }),

  getLlmStats: (hours: number = 24) =>
    apiRequest<HotelBullLlmStats>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/llm-stats',
      params: { hours },
    }),

  getBotHealth: () =>
    apiRequest<HotelBullBotHealth>(coreClient, {
      method: 'GET',
      url: '/hotel-bull/bot-health',
    }),
};

export default hotelBullApi;
